import { useState } from 'react';
import { getSupabase } from '../client';

interface MfaEnrollWizardProps {
  onSuccess?: () => void;
  onCancel?: () => void;
  issuer?: string;
  friendlyName?: string;
  /**
   * Label del botón de cancelación. Default: 'Cancelar' (cancela acción in-flight,
   * Dashboard.tsx lo usa así). AuthGate pasa 'Cerrar sesión' porque `forceEnroll`
   * no permite continuar sin MFA — la única opción real es logout.
   */
  cancelLabel?: string;
}

type Phase = 'intro' | 'enrolling' | 'awaiting_code' | 'verifying' | 'done' | 'error';

interface EnrollData {
  factorId: string;
  qrSvg: string;
  secret: string;
  uri: string;
}

/**
 * Wizard de enrollment TOTP.
 *
 * Refactorizado para EVITAR auto-enroll en cada mount: el user debe click
 * "Comenzar" para crear el factor. Cada re-mount del wizard (por cambios en
 * AuthGate / useMfaStatus) NO crea un nuevo factor. El límite de 10 factors
 * por user no se llena por mounts repetidos.
 *
 * Flujo:
 *   1. Phase 'intro' (default): muestra explicación + botón "Comenzar"
 *   2. Click → enroll → 'awaiting_code' con QR
 *   3. User escribe 6 dígitos → 'verifying' → 'done'
 */
export function MfaEnrollWizard({
  onSuccess,
  onCancel,
  issuer = 'Aosenuma',
  friendlyName = 'Aosenuma',
  cancelLabel = 'Cancelar',
}: MfaEnrollWizardProps) {
  const [phase, setPhase] = useState<Phase>('intro');
  const [data, setData] = useState<EnrollData | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  // True cuando detectamos un factor verified pre-existente en pre-flight.
  // En ese caso saltamos el enroll y solo pedimos los 6 dígitos del factor
  // que el user ya tiene configurado en su app TOTP (no mostramos QR).
  const [existingFactor, setExistingFactor] = useState(false);

  async function startEnrollment() {
    setError(null);
    setPhase('enrolling');
    const sb = getSupabase();

    try {
      // 1. Pre-flight: detectar si ya hay factor verified. Si sí, NO llamar
      //    enroll (el server responderá 403 "AAL2 required to enroll a new
      //    factor" porque la sesión es AAL1). En su lugar, mostramos
      //    challenge directo del factor existente.
      type F = { id: string; status: string; factor_type?: string };
      let verifiedExisting: F | null = null;
      try {
        const { data: factors } = await sb.auth.mfa.listFactors();
        const f = factors as unknown as { all?: F[]; totp?: F[] };
        const allFactors = f.all ?? f.totp ?? [];
        verifiedExisting = allFactors.find((x) => x.status === 'verified') ?? null;
        // Limpiar unverified previos (evita 422 mfa_factor_name_conflict y
        // respeta el límite de 10 factors per user).
        const stale = allFactors.filter((x) => x.status !== 'verified');
        for (const factor of stale) {
          await sb.auth.mfa.unenroll({ factorId: factor.id }).catch(() => undefined);
        }
      } catch {
        // listFactors falla silently — continuamos al enroll (si el server
        // tiene factor verified, retornará 403 y caerá al catch de abajo
        // que ahora interpreta ese error específico).
      }

      if (verifiedExisting) {
        // Switch a phase challenge: el user ya tiene factor, solo necesita
        // los 6 dígitos para upgrade a aal2. El padre (AuthGate vía
        // MfaUnlockModal) idealmente nunca llega aquí, pero defensivamente
        // lo manejamos por si alguien usa el wizard standalone.
        setData({
          factorId: verifiedExisting.id,
          qrSvg: '',
          secret: '',
          uri: '',
        });
        setExistingFactor(true);
        setPhase('awaiting_code');
        return;
      }

      // 2. Enroll con friendlyName único (defensivo, por si la limpieza falló)
      const uniqueSuffix = Math.random().toString(36).slice(2, 8);
      const uniqueName = `${friendlyName}-${uniqueSuffix}`;

      const { data: enrollData, error: err } = await sb.auth.mfa.enroll({
        factorType: 'totp',
        friendlyName: uniqueName,
        issuer,
      });

      if (err || !enrollData) {
        console.error('[MfaEnrollWizard] enroll error:', err);
        // Mensaje útil cuando el server rechaza por factor ya existente
        // (puede pasar si listFactors falló pre-flight pero el factor existe).
        const msg = err?.message ?? 'Error iniciando enrollment';
        const isAal2Required = /AAL2 required|aal2/i.test(msg);
        setError(
          isAal2Required
            ? 'Ya tienes un MFA configurado. Cierra sesión y vuelve a entrar ingresando tu código de 6 dígitos.'
            : msg,
        );
        setPhase('error');
        return;
      }

      setData({
        factorId: enrollData.id,
        qrSvg: enrollData.totp.qr_code,
        secret: enrollData.totp.secret,
        uri: enrollData.totp.uri,
      });
      setPhase('awaiting_code');
    } catch (e) {
      console.error('[MfaEnrollWizard] enroll threw:', e);
      setError(e instanceof Error ? e.message : String(e));
      setPhase('error');
    }
  }

  async function verifyCode() {
    if (!data || code.length !== 6) return;
    setPhase('verifying');
    setError(null);
    const sb = getSupabase();
    try {
      const { data: chData, error: chErr } = await sb.auth.mfa.challenge({ factorId: data.factorId });
      if (chErr || !chData) throw new Error(chErr?.message ?? 'No se pudo crear challenge');

      const { error: vErr } = await sb.auth.mfa.verify({
        factorId: data.factorId,
        challengeId: chData.id,
        code,
      });
      if (vErr) throw new Error(vErr.message);

      const { error: rpcErr } = await sb.rpc('mark_mfa_enrolled');
      if (rpcErr) {
        console.warn('[mfa] mark_mfa_enrolled RPC failed:', rpcErr.message);
      }

      setPhase('done');
      onSuccess?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setPhase('awaiting_code');
    }
  }

  return (
    <div style={wrapStyle}>
      <div style={cardStyle}>
        <h2 style={{ marginTop: 0, color: '#0f172a', fontSize: 22, fontWeight: 600 }}>
          Configura autenticación MFA
        </h2>
        <p style={{ color: '#334155', lineHeight: 1.5 }}>
          Por seguridad, los datos sensibles del proyecto requieren un segundo factor.
          Configura tu app de autenticación (Google Authenticator, Authy, 1Password) ahora.
        </p>

        {phase === 'intro' && (
          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <button type="button" onClick={startEnrollment} style={btnStyle}>
              Comenzar
            </button>
            {onCancel && (
              <button type="button" onClick={onCancel} style={{ ...btnStyle, ...btnGhost }}>
                {cancelLabel}
              </button>
            )}
          </div>
        )}

        {phase === 'enrolling' && <p style={{ color: '#334155' }}>Generando código…</p>}

        {phase === 'error' && (
          <>
            <p style={{ color: '#dc2626', fontWeight: 500 }}>Error: {error}</p>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" onClick={() => { setError(null); setPhase('intro'); }} style={btnStyle}>
                Reintentar
              </button>
              {onCancel && (
                <button type="button" onClick={onCancel} style={{ ...btnStyle, ...btnGhost }}>
                  {cancelLabel}
                </button>
              )}
            </div>
          </>
        )}

        {(phase === 'awaiting_code' || phase === 'verifying') && data && (
          <>
            {existingFactor ? (
              <p style={{ color: '#0f172a', marginTop: 8, lineHeight: 1.5 }}>
                Ya tienes MFA configurado. Ingresa el código actual de 6 dígitos
                de tu app autenticadora (Google Authenticator, Authy, 1Password)
                para reforzar tu sesión.
              </p>
            ) : (
              <>
                <div style={{ display: 'flex', justifyContent: 'center', margin: '16px 0' }}>
                  <div
                    aria-label="QR para autenticación MFA"
                    style={{ width: 220, height: 220, background: '#fff' }}
                    dangerouslySetInnerHTML={{ __html: data.qrSvg }}
                  />
                </div>
                <details style={{ fontSize: 13, color: '#475569' }}>
                  <summary style={{ cursor: 'pointer', color: '#1d4ed8' }}>
                    ¿No puedes escanear? Ingresa la clave manualmente
                  </summary>
                  <code style={{
                    display: 'block', wordBreak: 'break-all', padding: 10,
                    background: '#f1f5f9', color: '#0f172a', marginTop: 8,
                    borderRadius: 4, fontSize: 13,
                  }}>
                    {data.secret}
                  </code>
                </details>
              </>
            )}

            <label style={{ display: 'block', marginTop: 16, fontWeight: 500, color: '#0f172a' }}>
              Código de 6 dígitos
            </label>
            <input
              type="text"
              inputMode="numeric"
              pattern="\d{6}"
              maxLength={6}
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="123456"
              style={inputStyle}
              disabled={phase === 'verifying'}
            />

            {error && <p style={{ color: '#dc2626', marginTop: 8, fontWeight: 500 }}>{error}</p>}

            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button
                type="button"
                onClick={verifyCode}
                disabled={code.length !== 6 || phase === 'verifying'}
                style={btnStyle}
              >
                {phase === 'verifying' ? 'Verificando…' : 'Verificar'}
              </button>
              {onCancel && (
                <button type="button" onClick={onCancel} style={{ ...btnStyle, ...btnGhost }}>
                  Más tarde
                </button>
              )}
            </div>
          </>
        )}

        {phase === 'done' && (
          <p style={{ color: '#16a34a', fontWeight: 600, fontSize: 16, marginTop: 16 }}>
            ✓ Autenticación configurada. Tu sesión ahora tiene segundo factor.
          </p>
        )}
      </div>
    </div>
  );
}

const wrapStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 9999,
  fontFamily: 'system-ui, -apple-system, sans-serif',
};
const cardStyle: React.CSSProperties = {
  background: '#fff',
  padding: 32,
  borderRadius: 12,
  maxWidth: 460,
  width: '90%',
  boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
};
const btnStyle: React.CSSProperties = {
  background: '#1d4ed8',
  color: '#fff',
  border: 'none',
  padding: '10px 20px',
  borderRadius: 6,
  fontSize: 15,
  fontWeight: 500,
  cursor: 'pointer',
};
const btnGhost: React.CSSProperties = {
  background: 'transparent',
  color: '#1d4ed8',
  border: '1px solid #1d4ed8',
};
const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '12px 14px',
  fontSize: 22,
  letterSpacing: 6,
  textAlign: 'center',
  border: '2px solid #cbd5e1',
  borderRadius: 6,
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  marginTop: 4,
  color: '#0f172a',
  background: '#ffffff',
  outline: 'none',
  boxSizing: 'border-box',
};
